import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { AdminApiService } from '../services/api'
import { AdminPromoCode, SubscriptionTier } from '../types/admin'
import CassetteLoader from '../components/CassetteLoader'
import { 
  MagnifyingGlassIcon,
  PlusIcon,
  ArrowDownTrayIcon,
  TagIcon,
  CheckCircleIcon,
  XCircleIcon,
  HashtagIcon,
  ClipboardDocumentIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

export function Promocodes() {
  const navigate = useNavigate()
  const [promocodes, setPromocodes] = useState<AdminPromoCode[]>([])
  const [loading, setLoading] = useState(true)
  const [, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalPromocodes, setTotalPromocodes] = useState(0)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [deletingCode, setDeletingCode] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ code: string; wasUsed: boolean } | null>(null)

  // Helper function to get default activation expiry (1 month from now)
  const getDefaultActivationExpiry = () => {
    return new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
  }

  // Copy promocode to clipboard
  const copyPromocode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code)
      toast.success(`Copied "${code}" to clipboard!`)
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = code
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      toast.success(`Copied "${code}" to clipboard!`)
    }
  }

  // Create promocode form state
  const [createForm, setCreateForm] = useState({
    code: '',
    premiumDuration: 30,
    activationExpiry: getDefaultActivationExpiry(),
    subscriptionTier: SubscriptionTier.ARTIST,
    description: ''
  })

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
      // Reset to page 1 when search changes
      setCurrentPage(1)
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [searchTerm])

  useEffect(() => {
    loadPromocodes()
  }, [currentPage, debouncedSearchTerm, selectedStatus])

  const loadPromocodes = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '20',
      })
      
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm)
      if (selectedStatus) params.append('status', selectedStatus)
      
      const data = await AdminApiService.getPromoCodes(params.toString())
      
      // Handle response safely with null checks
      if (data) {
        setPromocodes(data.promoCodes || [])
        setTotalPages(data.totalPages || 1)
        setTotalPromocodes(data.total || 0)
      } else {
        console.error('Unexpected API response format:', data)
        setPromocodes([])
        setTotalPages(1)
        setTotalPromocodes(0)
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load promocodes'
      setError(errorMessage)
      toast.error(errorMessage)
      
      // Reset state on error
      setPromocodes([])
      setTotalPages(1)
      setTotalPromocodes(0)
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePromocode = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      // Ensure activationExpiry is in proper ISO format
      const formData = {
        ...createForm,
        activationExpiry: createForm.activationExpiry ? new Date(createForm.activationExpiry).toISOString() : ''
      }
      
      await AdminApiService.createPromoCode(formData)
      toast.success('Promocode created successfully')
      setShowCreateModal(false)
      setCreateForm({
        code: '',
        premiumDuration: 30,
        activationExpiry: getDefaultActivationExpiry(),
        subscriptionTier: SubscriptionTier.ARTIST,
        description: ''
      })
      loadPromocodes()
    } catch (err: any) {
      console.error('Promocode creation error:', err)
      const errorMessage = err.response?.data?.message || err.message || 'Failed to create promocode'
      toast.error(errorMessage)
    }
  }

  const handleExport = async () => {
    try {
      const params = new URLSearchParams()
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm)
      if (selectedStatus) params.append('status', selectedStatus)
      
      const blob = await AdminApiService.exportPromoCodes(params.toString())
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-')
      link.download = `cassette-promocodes-export-${timestamp}.csv`
      
      // Trigger download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Promocodes exported successfully')
    } catch (err: any) {
      toast.error('Failed to export promocodes')
      console.error('Export error:', err)
    }
  }

  const handleDeletePromocode = async (code: string) => {
    try {
      setDeletingCode(code)
      await AdminApiService.deletePromoCode(code)
      toast.success('Promocode deleted successfully')
      await loadPromocodes() // Reload the list
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete promocode'
      toast.error(errorMessage)
      console.error('Error deleting promocode:', err)
    } finally {
      setDeletingCode(null)
      setDeleteConfirm(null)
    }
  }

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setCreateForm({ ...createForm, code: result })
  }

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      disabled: 'bg-gray-100 text-gray-800',
      used: 'bg-blue-100 text-blue-800'
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status as keyof typeof styles] || styles.disabled}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    )
  }

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '0001-01-01T00:00:00Z' || dateString === '') {
      return 'Never'
    }
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }


  if (loading && promocodes.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <CassetteLoader size={80} text="Loading promocodes..." />
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white">
              <TagIcon className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Promocode Management</h1>
              <p className="text-sm text-gray-500">Create and manage promocodes for discounts and special offers</p>
            </div>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary w-full sm:w-auto"
          >
            <PlusIcon className="h-4 w-4" />
            Create Promocode
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="flex-1 sm:max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search promocodes by code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="input pl-10"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="sm:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="input"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExport}
            className="btn btn-primary btn-sm w-full sm:w-auto"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
              <TagIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Promocodes</p>
              <p className="text-2xl font-bold text-gray-900">{totalPromocodes}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Active</p>
              <p className="text-2xl font-bold text-gray-900">
                {promocodes.filter(p => !p.used && (!p.activationExpiry || new Date(p.activationExpiry) > new Date())).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Expired</p>
              <p className="text-2xl font-bold text-gray-900">
                {promocodes.filter(p => p.activationExpiry && new Date(p.activationExpiry) <= new Date()).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
              <HashtagIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Uses</p>
              <p className="text-2xl font-bold text-gray-900">
                {promocodes.filter(p => p.used).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Promocodes Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-hidden">
          <div className="overflow-x-auto min-w-full">
            <table className="table w-full">
              <thead>
                <tr>
                  <th className="min-w-[120px]">Code</th>
                  <th className="min-w-[120px]">Tier</th>
                  <th className="min-w-[140px]">Premium Duration</th>
                  <th className="min-w-[80px]">Used</th>
                  <th className="min-w-[100px]">Status</th>
                  <th className="min-w-[160px]">User</th>
                  <th className="min-w-[140px]">Activated</th>
                  <th className="min-w-[140px]">Expires</th>
                  <th className="min-w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {promocodes.map((promocode) => (
                  <tr key={promocode.code} className="hover:bg-gray-50">
                    <td className="min-w-[120px]">
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono bg-gray-100 px-2 py-1 rounded whitespace-nowrap">
                          {promocode.code}
                        </code>
                        <button
                          onClick={() => copyPromocode(promocode.code)}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          title={`Copy "${promocode.code}" to clipboard`}
                        >
                          <ClipboardDocumentIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    <td className="min-w-[120px]">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        promocode.subscriptionTier === SubscriptionTier.ARTIST ? 'bg-blue-100 text-blue-800' :
                        promocode.subscriptionTier === SubscriptionTier.STUDIO ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {(promocode.subscriptionTier || SubscriptionTier.ARTIST).charAt(0).toUpperCase() + (promocode.subscriptionTier || SubscriptionTier.ARTIST).slice(1)}
                      </span>
                    </td>
                    <td className="text-sm font-medium text-gray-900 min-w-[140px]">
                      {promocode.premiumDuration ? `${promocode.premiumDuration} days` : 'N/A'}
                    </td>
                    <td className="text-sm text-gray-900 min-w-[80px]">
                      {promocode.used ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Yes
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          No
                        </span>
                      )}
                    </td>
                    <td className="min-w-[100px]">
                      {getStatusBadge(promocode.status)}
                    </td>
                    <td className="text-sm text-gray-900 min-w-[160px]">
                      {promocode.userEmail ? (
                        <button
                          onClick={() => navigate(`/users/${promocode.userId}`)}
                          className="text-primary-600 hover:text-primary-900 hover:underline font-medium"
                        >
                          {promocode.userEmail}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="text-sm text-gray-900 min-w-[140px] whitespace-nowrap">
                      {promocode.used && promocode.activatedAt ? formatDate(promocode.activatedAt) : 'Not activated'}
                    </td>
                    <td className="text-sm text-gray-900 min-w-[140px] whitespace-nowrap">
                      {promocode.activationExpiry ? formatDate(promocode.activationExpiry) : 'Never'}
                    </td>
                    <td className="min-w-[100px]">
                      <button
                        onClick={() => setDeleteConfirm({ code: promocode.code, wasUsed: promocode.used })}
                        disabled={deletingCode === promocode.code}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                        title="Delete promocode"
                      >
                        {deletingCode === promocode.code ? (
                          <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <TrashIcon className="h-4 w-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalPromocodes)} of {totalPromocodes} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary btn-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Promocode Modal - Rendered using Portal to document body */}
      {showCreateModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-[9999] flex items-center justify-center">
          <div className="relative p-6 border-0 w-96 shadow-2xl rounded-lg bg-white mx-4 max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Promocode</h3>
              <form onSubmit={handleCreatePromocode} className="space-y-4">
                <div>
                  <label className="label">Promocode</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={createForm.code}
                      onChange={(e) => setCreateForm({ ...createForm, code: e.target.value.toUpperCase() })}
                      className="input flex-1"
                      placeholder="ENTER CODE"
                      required
                    />
                    <button
                      type="button"
                      onClick={generateRandomCode}
                      className="btn btn-secondary"
                    >
                      Generate
                    </button>
                    {createForm.code && (
                      <button
                        type="button"
                        onClick={() => copyPromocode(createForm.code)}
                        className="btn btn-ghost btn-sm"
                        title={`Copy "${createForm.code}" to clipboard`}
                      >
                        <ClipboardDocumentIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="label">Subscription Tier</label>
                  <select
                    value={createForm.subscriptionTier}
                    onChange={(e) => setCreateForm({ ...createForm, subscriptionTier: e.target.value as SubscriptionTier })}
                    required
                    className="input"
                  >
                    <option value={SubscriptionTier.ARTIST}>Artist Tier</option>
                    <option value={SubscriptionTier.STUDIO}>Studio Tier</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose subscription tier. Duration applies to both tiers.
                  </p>
                </div>

                <div>
                  <label className="label">Premium Duration (Days)</label>
                  <input
                    type="number"
                    value={createForm.premiumDuration}
                    onChange={(e) => setCreateForm({ ...createForm, premiumDuration: Number(e.target.value) })}
                    className="input"
                    placeholder="30"
                    required
                    min="1"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Number of days this promocode grants the selected tier
                  </p>
                </div>

                <div>
                  <label className="label">Activation Expiry</label>
                  <input
                    type="datetime-local"
                    value={createForm.activationExpiry}
                    onChange={(e) => setCreateForm({ ...createForm, activationExpiry: e.target.value })}
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    When this promocode expires and can no longer be activated (optional)
                  </p>
                </div>

                <div>
                  <label className="label">Description</label>
                  <textarea
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                    className="input"
                    rows={3}
                    placeholder="Optional description..."
                  />
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    Create Promocode
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
                <TrashIcon className="w-6 h-6 text-red-600" />
              </div>
            </div>
            
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Delete Promocode
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete promocode <strong>{deleteConfirm.code}</strong>?
                {deleteConfirm.wasUsed && (
                  <span className="block mt-2 text-orange-600 font-medium">
                    ⚠️ This promocode is currently in use. The user's premium access will be revoked.
                  </span>
                )}
                This action cannot be undone.
              </p>
              
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeletePromocode(deleteConfirm.code)}
                  disabled={deletingCode === deleteConfirm.code}
                  className="btn btn-danger btn-sm"
                >
                  {deletingCode === deleteConfirm.code ? 'Deleting...' : 'Delete Promocode'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}